import { Component, AfterViewInit, inject, signal, WritableSignal, computed, Inject, PLATFORM_ID, ChangeDetectorRef, viewChild, Signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AddressService, NavegoAddressResponse } from '../../shared/address.service';
import { PersonalInformation } from './signup.types';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { AccountService } from '../../shared/account.service';
import { Router, RouterLink } from '@angular/router';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CUSTOM_DATE_FORMATS } from '../../shared/constants/date-formtats';
import { LocalStorageService } from '../../shared/local-storage.service';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { Session } from '../../shared/types/session.type';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { ArrayOptionsItem, NewAccountPayload, AccountSettings } from '../../shared/types/account.types';
import { SignupVerificationRecord, SignupVerificationState } from './signup.types';
import { PaymentMethodService } from '../../settings/billing/payment-method/payment-method.service';
import { MatRadioModule } from '@angular/material/radio';
import { PersonalInformationSignupComponent } from './personal-information-signup/personal-information-signup.component';
import { CheckAddressSignupComponent } from './check-address-signup/check-address-signup.component';
import { PaymentMethodSignupComponent } from './payment-method-signup/payment-method-signup.component';
import { ProfileService } from '../../settings/account/profile/profile.service';
import { finalize, tap } from 'rxjs';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { ApiResponse } from '../../shared/common/types';
import { HTTP_STATUS_CODES } from '../../shared/constants/errors';
import { checkSignupCouponCodes } from '../../shared/common/singup.utils';
import { PromotionsService } from '../../shared/promotions.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';
import { DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS, DataLayer_SignUpFunnel_Pickup } from '../../shared/data-layer/data-layer.types';
import { AuthService } from '../auth.service';
import { TurnstileComponent } from '../../shared/turnstile/turnstile.component';
import { EmailVerificationComponent } from '../../shared/security/email-verification.component';
import { EmailVerificationService, VERIFICATION_TOKEN_QUERY_PARAM } from '../../shared/security/email-verification.service';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MarketingReferral } from '../../shared/types/common.types';
import { turnstilePendingMessage } from '../../shared/constants/errors';

@Component({
  selector: 'app-signup',
  providers: [provideNativeDateAdapter(),
  { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS.MONTH_AND_YEAR },
    PaymentMethodService,
    ProfileService,
    EmailVerificationService
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    RouterLink,
    MatRadioModule,
    CheckAddressSignupComponent,
    PersonalInformationSignupComponent,
    PaymentMethodSignupComponent,
    NgbTooltip,
    TurnstileComponent,
    EmailVerificationComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})

export class SignupComponent implements AfterViewInit {

  //#region Services

  private signalsStoreService = inject(SignalsStoreService);

  private addressService = inject(AddressService);

  localStorageService = inject(LocalStorageService);

  accountService = inject(AccountService);

  cdr = inject(ChangeDetectorRef);

  #router = inject(Router);

  #modalContentService = inject(ModalContentService);

  #location = inject(Location);

  #promotionsService = inject(PromotionsService);

  #notificationService = inject(NotificationService);

  #dataLayerService = inject(DataLayerService);

  #authService = inject(AuthService);
  #activatedRoute = inject(ActivatedRoute);
  verification = inject(EmailVerificationService);

  /** Kill switch for the email-verification step. Mirrors the backend's own
   *  parameter — see the comment on the flag in environment.ts. */
  readonly requiresEmailVerification = environment.config.flows.signup.requiresEmailVerification;

  showVerifiedBanner = signal(false);

  /** Copy of the navegoAddress restored from the verification record. The
   *  address autocomplete wipes the AddressService signal ~500ms after it
   *  initialises (autocomplete-input ngAfterViewInit:87), so it has to be
   *  re-asserted at submit time when the customer arrived through the link. */
  #restoredNavegoAddress: any | null = null;

  /** Turnstile guards POST /account (DEV-11124). An anonymous explorer never
   *  reaches that endpoint — they go to /explorer/activate, which the backend
   *  guards with a verificationToken instead — so the check below must not
   *  apply to them, or they would be stranded waiting for a token nobody is
   *  going to ask for. */
  turnstile = viewChild(TurnstileComponent);
  turnstileToken = signal<string | null>(null);

  //#endregion

  //#region Properties

  matStepper = viewChild.required<MatStepper>(MatStepper);

  personalInformationComponent: Signal<PersonalInformationSignupComponent> = viewChild.required('personalInformationComponent');

  firstStep = viewChild.required<MatStep>('firstStep');

  checkAddressComponent: Signal<CheckAddressSignupComponent> = viewChild.required('checkAddressComponent');

  signupEmail: string = '';

  pickupInfo: DataLayer_SignUpFunnel_Pickup | null = null;

  isLimitedUser = computed(() => this.signalsStoreService.isLimitedUser());

  isAnonymousExplorer = computed(() => this.signalsStoreService.isAnonymousExplorer());

  formValidityPersonalInformation: boolean | undefined;

  formValidityShoppingPreferences: boolean | undefined;

  formValidityPaymentMethod: boolean | undefined;

  deliveryAddressInformation: any;

  deliverySelectedDay: any;

  deliverySelectedTimeWindow: any;

  deliveryOptionSelected: any;

  personalInformationData: any;

  shopforValue: any;

  dietaryRestrictionsValues: any;

  paymentMethodStepControl: FormControl = new FormControl(true);

  personalInformation: WritableSignal<PersonalInformation | null> = signal(null);

  skipPaymentMethodStep: boolean = false;

  isLoadingContent: any = this.addressService.isLoading;

  isWaitingResponse: any = signal(false);

  shouldDisableCreateClientButton = signal(false);

  shouldSetUpSettings: boolean = true;

  subscriptionCard: WritableSignal<any[]> = signal([]);

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  hasGlobalMessages = computed(() => !!this.signalsStoreService.globalMessages().length);

  signupPromotionInfo = computed(() => this.signalsStoreService.signupPromotionInfo());

  isLoadedMarketingValidations = computed(() => this.signalsStoreService.isLoadedMarketingValidation());

  isTryingToApplyCoupon = signal(false);

  signupPromotionInfoFromLS = computed(() => this.signalsStoreService.signupPromotionInfoLS());

  //#endregion

  //#region Constructor

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const signupSelectedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP);
    if (signupSelectedMembership?.id)
      this.deliveryOptionSelected = signupSelectedMembership;

    const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any
    const hasPaymentMethod = !!(sessionStored)?.settings?.hasPaymentMethod;
    const isExplorer = !!sessionStored?.settings?.isAnonymousExplorer;

    // An explorer has a session but has never been through any of this, so they
    // start at step 0 like a new customer — not at the payment step, which is
    // where a returning customer without a card lands (DEV-11262).
    const stepToLoad = !isExplorer && this.signalsStoreService.hasSession() && !hasPaymentMethod ? 2 : 0;


    // Load first step or payment method step if hasSession
    if (stepToLoad > 0) {
      for (let index = 0; index < stepToLoad; index++) {
        this.matStepper().steps.toArray()[index].completed = true;
      }

      this.matStepper().selectedIndex = stepToLoad;
    }

    // An anonymous explorer gave us their address when they started shopping,
    // and /explorer/activate only accepts that same one — so seed it instead of
    // making them retype an address they are not allowed to change.
    if (isExplorer) {
      const explorerEmail = sessionStored?.accountInfo?.email || '';
      if (explorerEmail) {
        this.signupEmail = explorerEmail;
        const checkAddress = this.checkAddressComponent();
        checkAddress?.signupEmail.set(explorerEmail);
        checkAddress?.isEmailAvailable.set(true);
      }
    }

    this.cdr.detectChanges();

    if (!this.requiresEmailVerification) return;

    // Leave with a full navigation, not router.navigate: this tab's signals
    // store has no session, so it has to re-read it from localStorage.
    this.verification.initSync(() => window.location.assign('/shop'));

    const verificationToken = this.#activatedRoute.snapshot.queryParamMap.get(VERIFICATION_TOKEN_QUERY_PARAM);
    // An anonymous explorer DOES have a session and still has to verify, so the
    // plain "no session" test would silently ignore their link.
    const canConsume = !this.signalsStoreService.hasSession() || this.isAnonymousExplorer();
    if (verificationToken && canConsume) this.#consumeVerificationToken(verificationToken);
  }

  //#endregion

  //#region Signup email verification

  #sendVerificationEmail(isFirstSend = false) {
    this.verification.send(this.signupEmail, this.#buildVerificationState(), isFirstSend);
  }

  resendVerificationEmail() {
    this.#sendVerificationEmail();
  }

  changeVerificationEmail() {
    // Back to step 1. Its state survives because the stepper is hidden, not
    // destroyed, so fixing a typo costs the customer nothing.
    this.verification.changeEmail();
    this.firstStep().completed = false;
  }

  #buildVerificationState(): SignupVerificationState {
    return {
      // `deliveryAddressInformation` holds the CHILD's signal, handed over by
      // (deliveryAddressChanged), and is undefined until it first emits.
      deliveryAddressInformation: this.deliveryAddressInformation?.() ?? null,
      deliverySelectedDay: this.deliverySelectedDay ?? null,
      deliverySelectedTimeWindow: this.deliverySelectedTimeWindow ?? null,
      deliveryOptionSelected: this.deliveryOptionSelected ?? null,
      navegoAddress: this.addressService.navegoAddress() ?? null,
      signupPromotionInfo: this.signupPromotionInfo() ?? null,
      // The attribution lives only in THIS browser's storage and the emailed
      // link carries no utm_ of its own. Without parking it, someone who opens
      // that link in another browser creates the account with no attribution
      // and whoever referred them is never credited.
      marketingReferral: (this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL) as MarketingReferral | null) ?? null,
    };
  }

  #consumeVerificationToken(token: string) {
    this.verification.consume(token).subscribe({
      next: record => {
        // A null record means the link is spent or bogus; the service has
        // already set the error state the shared screen renders.
        if (!record) return this.cdr.detectChanges();
        this.#restoreFromVerification(token, record as SignupVerificationRecord);
      }
    });
  }

  #restoreFromVerification(token: string, record: SignupVerificationRecord) {
    const { email, state } = record;

    this.verification.markVerified(token, email);
    this.signupEmail = email;

    // Assigning a signal of our own detaches this from the child until it emits
    // again — which is what we want: the child is on a step we are skipping.
    this.deliveryAddressInformation = signal(state?.deliveryAddressInformation ?? {});
    this.deliverySelectedDay = state?.deliverySelectedDay ?? null;
    this.deliverySelectedTimeWindow = state?.deliverySelectedTimeWindow ?? null;
    this.deliveryOptionSelected = state?.deliveryOptionSelected ?? null;

    if (state?.navegoAddress) {
      this.#restoredNavegoAddress = state.navegoAddress;
      this.addressService.navegoAddress.set(state.navegoAddress);
    }

    if (state?.signupPromotionInfo && !this.signupPromotionInfo())
      this.signalsStoreService.setSignupPromotionInfo(state.signupPromotionInfo);

    // Only when this browser has none of its own: an attribution captured in
    // THIS session is newer than the parked copy and must not be overwritten.
    if (state?.marketingReferral && !this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL))
      this.localStorageService.set(LOCALSTORAGE_KEYS.MARKETING_REFERRAL, state.marketingReferral);

    if (state?.deliveryOptionSelected)
      this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP, state.deliveryOptionSelected);

    // Prefill the step-1 child too, so a bounce back to it (a full time slot)
    // keeps the verified address in place.
    const checkAddress = this.checkAddressComponent();
    checkAddress?.signupEmail.set(email);
    checkAddress?.isEmailAvailable.set(true);

    this.firstStep().completed = true;
    this.matStepper().selectedIndex = 1;
    this.firstStep().editable = false;
    if (this.shouldSetUpSettings) {
      this.getAccountSettings();
      this.shouldSetUpSettings = false;
    }

    this.showVerifiedBanner.set(true);
    this.cdr.detectChanges();
  }

  //#endregion

  //#region Explorer flow (DEV-11262)

  /**
   * Registers an anonymous explorer, in place of creating an account.
   *
   * The payload is the ordinary one; what differs is the endpoint, and that the
   * reply carries a **new token** which `activateExplorerUser` installs before
   * anything else runs. The card step that follows is the standard one.
   */
  #activateExplorer() {
    const payload = this.setUpNewClientPayload();
    if (!payload) return;

    this.shouldDisableCreateClientButton.set(true);
    this.#trackAccountDetailsFunnelEvent();

    this.accountService
      .activateExplorerUser(payload)
      .pipe(finalize(() => this.shouldDisableCreateClientButton.set(false)))
      .subscribe({
        next: (res: ApiResponse<Session>) => {
          if ((res as any)?.error) return;
          if (!this.deliveryOptionSelected?.configuration?.requireAddress)
            this.accountService.setMustFetchStates();
          this.matStepper().next();
        }
      });
  }

  //#endregion

  //#region Methods

  continue() {
    // Step 1 no longer advances on its own: the address has to be proved first.
    // An anonymous explorer goes through the same gate — the backend guards
    // /explorer/activate with the token exactly as it guards /account.
    if (this.requiresEmailVerification && !this.verification.isVerified(this.signupEmail)) {
      this.#sendVerificationEmail(true);
      return;
    }

    this.#advanceToPersonalInformation();
  }

  #advanceToPersonalInformation() {

    this.firstStep().completed = true;
    this.matStepper().next();

    const { selectedIndex } = this.matStepper();

    if (selectedIndex === 1) {
      this.firstStep().editable = false;
      if (this.shouldSetUpSettings) {
        this.getAccountSettings();
        this.shouldSetUpSettings = false;
      }
    }
  }

  createNewClient() {
    if (this.shouldDisableCreateClientButton()) return;

    // The autocomplete nulls navegoAddress ~500ms after it initialises, so a
    // signup resumed from the email link would otherwise create an account with
    // no address at all. Re-assert before anything reads it.
    if (this.#restoredNavegoAddress && !this.addressService.navegoAddress())
      this.addressService.navegoAddress.set(this.#restoredNavegoAddress);

    // An anonymous explorer already has the account this step would create —
    // `POST /account` would answer 409. They go to /explorer/activate instead,
    // with this very same payload (DEV-11262).
    if (this.isAnonymousExplorer()) {
      this.#activateExplorer();
      return;
    }

    if (TurnstileComponent.enabled && !this.turnstileToken()) {
      this.#notificationService.show({
        text: turnstilePendingMessage(this.turnstile()?.isInteractive()),
        type: 'warning'
      });
      return;
    }

    this.shouldDisableCreateClientButton.set(true);
    const payload = this.setUpNewClientPayload();
    if (!payload) return;

    this.#trackAccountDetailsFunnelEvent();

    this.accountService
      .create(payload)
      .pipe(
        finalize(() => {
          this.shouldDisableCreateClientButton.set(false);
          // The token was spent on this request whatever the outcome; `next`
          // does not run on a failure, so the refresh belongs here (DEV-11124).
          this.turnstile()?.reset();
        })
      )
      .subscribe({
        next: (res: ApiResponse<Session>) => {
          const { data, status } = res;
          if (status === HTTP_STATUS_CODES.CONFLICT)
            return this.#showModalEmailAlreadyInUse(payload.accountInfo.email);
          if (data?.isTimeSlotCapacityFull) {
            this.firstStep().editable = true;
            this.matStepper().previous();
          } else if (data) {
            if (!this.deliveryOptionSelected.configuration.requireAddress)
              this.accountService.setMustFetchStates();
            this.matStepper().next();
            this.shouldDisableCreateClientButton.set(false);
          }
        }
      });
  }

  #trackAccountDetailsFunnelEvent() {
    const info = this.personalInformationData();
    if (!info) return;
    const requireAddress = !!this.deliveryOptionSelected?.configuration?.requireAddress;
    const nav = this.addressService.navegoAddress();
    if (requireAddress && !nav) return;
    this.#dataLayerService.trackSignUpFunnelAccountDetailsEvent({
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.account_details,
      funnel_step_number: 2,
      delivery_type: requireAddress ? 'delivery' : 'pickup',
      ...(requireAddress
        ? { delivery: { zip_code: this.deliveryAddressInformation()?.zipCode ?? '', zone: nav!.zoneName, in_area: true } }
        : { pickup: this.pickupInfo ?? { location: '', day: '', zone: '' } }),
      user: {
        email: info.email ?? '',
        phone: info.phone ?? '',
        first_name: info.firstName ?? '',
        last_name: info.lastName ?? '',
      },
    });
  }

  #trackCompleteFunnelEvent() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session?.accountInfo) return;
    const requireAddress = !!this.deliveryOptionSelected?.configuration?.requireAddress;
    const nav = this.addressService.navegoAddress();
    this.#dataLayerService.trackSignUpFunnelCompleteEvent({
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.sign_up_complete,
      funnel_step_number: 3,
      delivery_type: requireAddress ? 'delivery' : 'pickup',
      ...(requireAddress
        ? { delivery: { zip_code: this.deliveryAddressInformation()?.zipCode ?? '', zone: nav?.zoneName ?? '', in_area: true } }
        : { pickup: this.pickupInfo ?? { location: '', day: '', zone: '' } }),
      user: {
        id: session.accountInfo.id.toString(),
        logged_in: true,
        customer_type: 'new',
        email: session.accountInfo.email,
        phone: session.accountInfo.phone,
        first_name: session.accountInfo.firstName,
        last_name: session.accountInfo.lastName,
      },
    });
  }

  #showModalEmailAlreadyInUse(email: string) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) {
          this.checkAddressComponent().clearEmailField();
          this.firstStep().editable = true;
          this.matStepper().selectedIndex = 0;
          return;
        }
        this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
        this.#router.navigate(['/login']);
      })
  }

  private getAccountSettings() {
    this.accountService.getAccountSettings().subscribe({
      next: (accountSettings: AccountSettings | null) => {
        if (accountSettings) {
          this.setUpAccountSettings(accountSettings);
        }
      }
    });
  }

  private setUpAccountSettings(accountSettings: AccountSettings) {
    this.personalInformation.set({
      contactPerson: {
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
      },
      dietaryRestrictions: accountSettings.dietaryPreferences?.map((preference: ArrayOptionsItem) => {
        return { ...preference, completed: false };
      }) || [],
      peopleShoppingFor: accountSettings.peopleShoppingFor || [],
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      phone: '',
      referralSource: '',
      advertisement: '',
      referredBy: '',
      shopfor: null,
      receiveNewsletter: true,
      receiveText: true
    });
  }

  private setUpNewClientPayload(): NewAccountPayload | null {
    try {
      const { firstName, lastName, phone, email, password, birthday, advertisement, referredBy, dietaryRestrictions = this.dietaryRestrictionsValues, contactPerson: contact, receiveNewsletter, receiveText, receiveTextDelivery } = this.personalInformationData() || {};
      const { additionalAddress: additional, zipCode: zip, deliveryNotes: noteDriver } = this.deliveryAddressInformation()
      const { street, zoneId, zoneName, city, state: stateCode, latitude, longitude } = this.addressService.navegoAddress() as NavegoAddressResponse || {}
      const res: any = {
        // Rides along for both paths: POST /account and, for an explorer,
        // POST /explorer/activate — the backend guards each of them with it.
        verificationToken: this.verification.token || undefined,
        turnstileToken: this.turnstileToken() || undefined,
        accountInfo: {
          dietaryPreferences: dietaryRestrictions?.filter((value: any) => value.completed).map((value: any) => { return { id: value.id, name: value.name } }) || null,
          email,
          firstName,
          lastName,
          noteDriver,
          password,
          phone,
          birthday,
          referredBy: referredBy || null
        },
        settings: {
          receiveNewsletter,
          receiveText,
          receiveTextDelivery,
          delivery: {
            type: this.deliveryOptionSelected.id,
            dayId: this.deliverySelectedDay.id,
            timeWindowId: this.deliverySelectedTimeWindow === -1 ? null : this.deliverySelectedTimeWindow
          },
          peopleShoppingForId: this.shopforValue
        },
        contact
      } as NewAccountPayload

      if (this.deliveryOptionSelected.configuration.requireAddress) {
        res.address = {
          additional: additional,
          city: city,
          latitude: latitude,
          longitude: longitude,
          stateCode: stateCode,
          street: street,
          zip: zip,
          zoneId: zoneId || this.deliverySelectedDay.zoneId
        }
      } else {
        res.address = {
          zoneId: this.deliverySelectedDay.zoneId
        }
      }

      return res;
    } catch (error) {
      console.log('error >>>', error);
      return null;
    }
  }

  navigateToWelcomePage() {
    // sign_up_complete fires here — when the payment method has been added
    // (this runs on the payment step's success), not at account creation.
    this.#trackCompleteFunnelEvent();
    this.#router.navigate([`/welcome`])
  }

  //#endregion

  applySignupCoupon(code: string) {

    if (!code) return this.#notificationService.show({ text: 'You should provide a coupon code.', type: 'error' });

    this.isTryingToApplyCoupon.set(true);

    const marketingReferral: MarketingReferral = {
      utmCampaign: code
    };

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalsStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: true
    }).pipe(
      tap(res => {
        if (res.name) {
          const currentPath = this.#location.path().split('?')[0];
          this.#location.replaceState(currentPath);
        }
      })
    ).subscribe();

  }

  clearSignupPromotionInfo() {
    this.signalsStoreService.setSignupPromotionInfo(null);
  }

  restoreSignupInfo() {
    const signupPromotionInfo = this.localStorageService.get(LOCALSTORAGE_KEYS.PROMOTION_INFO);
    if (!signupPromotionInfo) return;
    this.signalsStoreService.setSignupPromotionInfo(signupPromotionInfo);
  }

}
